import { FormEvent, useMemo, useState } from 'react';

import { Button } from '../shared/Button';
import { Modal } from '../shared';

import { type EventItem, useActivations, useEvents } from '../hooks/events';

import { handleError } from '../utils/utils';

import styles from './Events.module.scss';
import { formatVersionRange, hasVersionBounds } from './versionRange';

type EventsViewProps = {
    onOpenSchedule: (eventId: string) => void;
};

export function EventsView({ onOpenSchedule }: EventsViewProps) {
    const {
        data: events = [],
        createEvent,
        deleteEvent,
        updateEventVersion,
    } = useEvents();
    const activationsQuery = useActivations();
    const { data: activations = [], isSuccess, isError } = activationsQuery;
    const [newEventKey, setNewEventKey] = useState('');
    const [newEventPortalCategoryId, setNewEventPortalCategoryId] =
        useState('');
    const [newMinimumVersion, setNewMinimumVersion] = useState('');
    const [newMaximumVersion, setNewMaximumVersion] = useState('');

    const eventIdsWithActivations = useMemo(
        () =>
            isSuccess ? new Set(activations.map((item) => item.eventId)) : null,
        [activations, isSuccess],
    );

    async function onCreateEvent(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (!newEventKey.trim()) return;

        try {
            await createEvent.mutateAsync({
                eventKey: newEventKey.trim(),
                portalCategoryId: newEventPortalCategoryId.trim() || null,
                minimumVersion: newMinimumVersion.trim() || undefined,
                maximumVersion: newMaximumVersion.trim() || undefined,
            });
            setNewEventKey('');
            setNewEventPortalCategoryId('');
            setNewMinimumVersion('');
            setNewMaximumVersion('');
        } catch (err) {
            handleError('Failed to create event', err);
        }
    }

    async function onDeleteEvent(eventId: string) {
        const isConfirmed = await Modal.confirm({
            heading: 'Delete event',
            text: 'Really delete this event? All activations and schedules in this event will also be deleted.',
            okLabel: 'Yes do it!',
        });
        if (!isConfirmed) return;

        try {
            await deleteEvent.mutateAsync(eventId);
        } catch (err) {
            handleError('Failed to delete event', err);
        }
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2>Events</h2>
                <p>
                    Create logical buckets for activations. Schedules are saved
                    per event. An event&apos;s version bounds are the default
                    for its activations — an activation overrides only the bound
                    it sets itself.
                </p>
            </div>
            {isError && (
                <div className={styles.errorMessage}>
                    Unable to load activations. Activation status warnings are
                    unavailable.
                </div>
            )}

            <form className={styles.groupsForm} onSubmit={onCreateEvent}>
                <div className={styles.formRowCompact}>
                    <input
                        className={styles.input}
                        placeholder="Event key (example: weekend-specials)"
                        value={newEventKey}
                        onChange={(e) => setNewEventKey(e.target.value)}
                    />
                    <input
                        className={styles.input}
                        placeholder="Portal category id (optional uuid)"
                        value={newEventPortalCategoryId}
                        onChange={(e) =>
                            setNewEventPortalCategoryId(e.target.value)
                        }
                    />
                    <div className={styles.formButtonWrap}>
                        <Button type="submit" color="primary">
                            Create event
                        </Button>
                    </div>
                </div>
                <div className={styles.formRowCompact}>
                    <input
                        className={styles.input}
                        placeholder="Default min version, inclusive (optional, e.g. 1.40.0)"
                        value={newMinimumVersion}
                        onChange={(e) => setNewMinimumVersion(e.target.value)}
                    />
                    <input
                        className={styles.input}
                        placeholder="Default max version, exclusive (optional, e.g. 1.40.0)"
                        value={newMaximumVersion}
                        onChange={(e) => setNewMaximumVersion(e.target.value)}
                    />
                </div>
            </form>

            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Event key</th>
                        <th>Portal category id</th>
                        <th>Default client version</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {events.map((event) => (
                        <EventRow
                            key={event.id}
                            event={event}
                            hasActivation={
                                eventIdsWithActivations === null ||
                                eventIdsWithActivations.has(event.id)
                            }
                            onOpenSchedule={onOpenSchedule}
                            onDelete={() => void onDeleteEvent(event.id)}
                            onSaveVersion={(minimumVersion, maximumVersion) =>
                                updateEventVersion.mutateAsync({
                                    id: event.id,
                                    dto: { minimumVersion, maximumVersion },
                                })
                            }
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function EventRow({
    event,
    hasActivation,
    onOpenSchedule,
    onDelete,
    onSaveVersion,
}: {
    event: EventItem;
    hasActivation: boolean;
    onOpenSchedule: (eventId: string) => void;
    onDelete: () => void;
    onSaveVersion: (
        minimumVersion?: string,
        maximumVersion?: string,
    ) => Promise<unknown>;
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [minimumVersion, setMinimumVersion] = useState(
        event.minimumVersion ?? '',
    );
    const [maximumVersion, setMaximumVersion] = useState(
        event.maximumVersion ?? '',
    );
    const [isSaving, setIsSaving] = useState(false);

    function startEditing() {
        setMinimumVersion(event.minimumVersion ?? '');
        setMaximumVersion(event.maximumVersion ?? '');
        setIsEditing(true);
    }

    async function onSave() {
        setIsSaving(true);
        try {
            await onSaveVersion(
                minimumVersion.trim() || undefined,
                maximumVersion.trim() || undefined,
            );
            setIsEditing(false);
        } catch (err) {
            handleError('Failed to update version defaults', err);
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <tr>
            <td>
                {event.eventKey}
                {!hasActivation && (
                    <span
                        className={styles.missingActivationWarning}
                        title="Create an activation key before scheduling this event — publications without one cannot be saved"
                    >
                        ⚠ Missing activation key
                    </span>
                )}
            </td>
            <td>{event.portalCategoryId || '-'}</td>
            <td>
                {isEditing ? (
                    <div className={styles.formRowCompact}>
                        <input
                            className={styles.input}
                            placeholder="Min (inclusive)"
                            value={minimumVersion}
                            onChange={(e) => setMinimumVersion(e.target.value)}
                        />
                        <input
                            className={styles.input}
                            placeholder="Max (exclusive)"
                            value={maximumVersion}
                            onChange={(e) => setMaximumVersion(e.target.value)}
                        />
                        <Button
                            type="button"
                            color="primary"
                            size="small"
                            onClick={() => void onSave()}
                            loading={isSaving}
                        >
                            Save
                        </Button>
                        <Button
                            type="button"
                            color="secondary"
                            size="small"
                            onClick={() => setIsEditing(false)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                    </div>
                ) : (
                    <button
                        type="button"
                        className={`${styles.versionBadgeButton} ${
                            hasVersionBounds(event)
                                ? styles.versionBadgeBounded
                                : styles.versionBadge
                        }`}
                        onClick={startEditing}
                        title="Click to edit"
                    >
                        {formatVersionRange(
                            event.minimumVersion,
                            event.maximumVersion,
                        )}
                    </button>
                )}
            </td>
            <td className={styles.actionCell}>
                <Button
                    type="button"
                    color="secondary"
                    size="small"
                    onClick={() => onOpenSchedule(event.id)}
                >
                    Open schedule
                </Button>
                <Button
                    type="button"
                    color="danger"
                    size="small"
                    onClick={onDelete}
                >
                    Delete
                </Button>
            </td>
        </tr>
    );
}
